// ============================================================
// SevenStars SmartCare Passport V3 — Unit Passport
// Sprint 1: QR-based access, public view, PIN protection,
//           activation flow, ownership transfer
// ============================================================
import { supabase } from '../store/supabase';
import React, { useState, useEffect, useCallback } from 'react';
import {
  passportStore, vehicleStore, customerStore,
  serviceRecordStore, serviceRequestStore,
  referralStore, promotionStore, transferCodeStore
} from '../store/data';
import {
  Shield, Wrench, Calendar, Gift, Tag, ChevronLeft,
  CheckCircle, X, AlertTriangle, Info, Clock, Phone,
  MapPin, Lock, ArrowRight, Upload
} from 'lucide-react';
import logo from '../logo.png';

const ISSUE_CATEGORIES = ['Battery', 'Brake', 'Motor', 'Charger', 'Wiring', 'Lights', 'General Check-up', 'Others'];
const DIAGNOSE = {
  'Unit is not starting': { causes: ['Battery may be fully discharged', 'Power switch issue', 'Fuse may be blown'], advice: ['Check battery indicator lights', 'Verify charger connection', 'Do NOT force start — book a technician'] },
  'Battery drains fast': { causes: ['Battery age or degradation', 'High load (hills, heavy cargo)', 'Possible cell imbalance'], advice: ['Charge fully before each trip', 'Allow battery to cool before charging', 'Book a battery health check'] },
  'Brake noise': { causes: ['Brake pads worn down', 'Debris between pad and disc'], advice: ['Avoid hard braking temporarily', 'Do NOT ride if brakes feel weak', 'Book immediate inspection'] },
  'Charger not working': { causes: ['Cable damaged', 'Outlet issue', 'Charging port clogged'], advice: ['Try a different outlet', 'Keep port dry and clean', 'Do not use non-original charger'] },
  'Motor sound': { causes: ['Loose motor mount', 'Bearing wear'], advice: ['Stop using immediately if grinding', 'Book technician inspection'] },
  'Lights not working': { causes: ['Loose wiring', 'Fuse issue', 'Low battery'], advice: ['Check battery charge', 'Do NOT ride at night without lights', 'Book electrical check'] },
  'Others': { causes: ['Technician assessment needed'], advice: ['Describe clearly when booking', 'Take a video if possible', 'Book general inspection'] },
};

// ── Helpers ──────────────────────────────────────────────────
function WarrantyBadge({ status }) {
  if (status === 'active')   return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">✓ Warranty Active</span>;
  if (status === 'expiring') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">⚠ Expiring Soon</span>;
  if (status === 'expired')  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">⚠ For Renewal</span>;
  return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">Unknown</span>;
}

// ── PIN Entry Component ──────────────────────────────────────
function PinEntry({ onSuccess, onCancel, title = 'Enter your PIN', subtitle = 'Enter your 6-digit security PIN to continue' }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  const handleDigit = (d) => {
    if (pin.length < 6) setPin(p => p + d);
  };
  const handleDelete = () => setPin(p => p.slice(0, -1));

  useEffect(() => {
    if (pin.length === 6) {
      const ok = onSuccess(pin);
      if (ok === false) {
        setError(`Incorrect PIN. ${3 - attempts - 1} attempts remaining.`);
        setAttempts(a => a + 1);
        setTimeout(() => setPin(''), 500);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-md mx-auto bg-white rounded-t-3xl pb-8">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <h3 className="font-bold text-slate-800 text-lg">{title}</h3>
            <p className="text-slate-500 text-sm mt-0.5">{subtitle}</p>
          </div>
          {onCancel && <button onClick={onCancel}><X size={22} className="text-slate-400" /></button>}
        </div>

        {/* PIN dots */}
        <div className="flex justify-center gap-3 py-5">
          {[0,1,2,3,4,5].map(i => (
            <div key={i} className="w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all"
              style={{ borderColor: i < pin.length ? '#1B4F72' : '#e2e8f0', background: i < pin.length ? '#1B4F72' : 'transparent' }}>
              {i < pin.length && <div className="w-3 h-3 rounded-full bg-white" />}
            </div>
          ))}
        </div>

        {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 px-8">
          {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((k, i) => (
            <button key={i}
              onClick={() => k === '⌫' ? handleDelete() : k !== '' ? handleDigit(String(k)) : null}
              className="h-14 rounded-2xl text-xl font-semibold transition-all active:scale-95"
              style={{
                background: k === '⌫' ? '#fee2e2' : k === '' ? 'transparent' : '#f8fafc',
                color: k === '⌫' ? '#dc2626' : '#1B4F72',
                cursor: k === '' ? 'default' : 'pointer',
              }}
            >
              {k}
            </button>
          ))}
        </div>

          <div className="text-center mt-4 space-y-1">
            <button
              className="text-sm font-medium"
              style={{ color: '#1B4F72' }}
              onClick={() => alert('Contact SevenStars at 0999 341 4528 — we will give you a temporary PIN to log in.')}
            >
              Forgot PIN?
            </button>
            <p className="text-xs text-slate-400">Call 0999 341 4528 for a temporary PIN</p>
          </div>
      </div>
    </div>
  );
}

// ── Activation Flow ──────────────────────────────────────────
function ActivationFlow({ passport, vehicle, onComplete, onCancel }) {
  // ── Activation Flow: Purchase Date Verification (free, no SMS needed)
  // TODO (V3.1): Replace verifyPurchaseDate with Semaphore SMS OTP when ready
  const [step, setStep] = useState(1); // 1=verify, 2=pin, 3=confirm, 4=done
  const [purchaseDate, setPurchaseDate] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);

  // Verify purchase date against what's on file
  // Admin override: entering 6-digit passport suffix also unlocks
  const verifyPurchaseDate = () => {
    if (!purchaseDate) { setError('Please enter your purchase date.'); return; }
    const entered = purchaseDate.trim();
    const onFile  = vehicle?.purchase_date || '';
    const normalize = (d) => {
      if (!d) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
      const mdy = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mdy) return `${mdy[3]}-${mdy[1].padStart(2,'0')}-${mdy[2].padStart(2,'0')}`;
      return d;
    };
    const passportSuffix = passport?.passport_id?.split('-').pop() || '';
    const isAdminOverride = entered === passportSuffix;
    if (normalize(entered) === normalize(onFile) || isAdminOverride) {
      setError(''); setStep(2);
    } else {
      const n = attempts + 1; setAttempts(n);
      if (n >= 3) setError('Too many attempts. Contact SevenStars: 0999 341 4528');
      else setError(`Incorrect date. ${3-n} attempt${3-n===1?'':'s'} left. Check your official receipt.`);
    }
  };

  const validatePin = () => {
    if (pin.length !== 6) { setError('PIN must be exactly 6 digits.'); return; }
    const simple = ['111111','222222','333333','444444','555555','666666',
                    '777777','888888','999999','000000','123456','654321'];
    if (simple.includes(pin)) { setError('PIN is too simple. Please choose a different one.'); return; }
    setError('');
    setStep(3);
  };

  const confirmAndActivate = async () => {
    if (confirmPin !== pin) { setError('PINs do not match. Please try again.'); setConfirmPin(''); return; }
    const pinHash = passportStore.hashPin(pin);
    await passportStore.activate(passport.token, pinHash);
    setStep(4);
    setTimeout(onComplete, 2000);
  };

  const steps = [
    { num: 1, label: 'Verify' },
    { num: 2, label: 'Create PIN' },
    { num: 3, label: 'Confirm' },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="min-h-full flex items-end justify-center">
        <div className="w-full max-w-md bg-white rounded-t-3xl px-6 pt-6 pb-10">

          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-bold text-slate-800 text-xl">Activate My Passport</h2>
              <p className="text-slate-500 text-sm mt-0.5">{passport.passport_id}</p>
            </div>
            <button onClick={onCancel}><X size={22} className="text-slate-400" /></button>
          </div>

          {/* Step indicators */}
          {step < 4 && (
            <div className="flex items-center gap-2 mb-6">
              {steps.map((s, i) => (
                <React.Fragment key={s.num}>
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                      style={{ background: step >= s.num ? '#1B4F72' : '#f1f5f9', color: step >= s.num ? 'white' : '#94a3b8' }}>
                      {step > s.num ? '✓' : s.num}
                    </div>
                    <span className="text-xs" style={{ color: step >= s.num ? '#1B4F72' : '#94a3b8' }}>{s.label}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex-1 h-0.5 mb-4 transition-all" style={{ background: step > s.num ? '#1B4F72' : '#f1f5f9' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Step 1: Verify purchase date */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl" style={{ background: '#eef3f8' }}>
                <p className="text-sm font-semibold mb-1" style={{ color: '#1B4F72' }}>
                  🛡️ Identity Verification
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  To verify that you are the registered owner, please enter the <strong>date you purchased your SevenStars e-bike</strong>. You can find this on your official receipt.
                </p>
              </div>

              {/* Show the vehicle model as context */}
              <div className="p-3 rounded-xl border border-slate-100 bg-slate-50 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#1B4F72' }}>
                  <span className="text-white text-sm">🛵</span>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Your unit</p>
                  <p className="font-bold text-slate-800 text-sm">{vehicle?.model || 'SevenStars E-Bike'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Purchase Date
                </label>
                <input
                  type="date"
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 text-base"
                  value={purchaseDate}
                  onChange={e => { setPurchaseDate(e.target.value); setError(''); }}
                  max={new Date().toISOString().split('T')[0]}
                  autoFocus
                />
                <p className="text-xs text-slate-400 mt-1 px-1">
                  Format: MM/DD/YYYY — check your receipt or warranty card
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-100">
                  <p className="text-red-600 text-sm">{error}</p>
                  {attempts >= 3 && (
                    <a href="tel:+639993414528" className="flex items-center gap-1 mt-2 text-sm font-semibold" style={{ color: '#1B4F72' }}>
                      📞 Call 0999 341 4528
                    </a>
                  )}
                </div>
              )}

              <button
                onClick={verifyPurchaseDate}
                disabled={!purchaseDate || attempts >= 3}
                className="w-full text-white font-semibold py-3 rounded-xl transition-all disabled:opacity-50"
                style={{ background: '#1B4F72' }}
              >
                Verify →
              </button>

              <p className="text-slate-400 text-xs text-center leading-relaxed">
                Can't remember the date? Contact SevenStars at <strong>0999 341 4528</strong> for help.
              </p>
            </div>
          )}

          {/* Step 2: Create PIN */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl" style={{ background: '#eef3f8' }}>
                <p className="text-sm font-semibold mb-1" style={{ color: '#1B4F72' }}>✅ Identity Verified!</p>
                <p className="text-sm text-slate-600">
                  Now create a <strong>6-digit PIN</strong> to protect your SmartCare Passport. You will need this PIN every time you access your account.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Your 6-Digit PIN</label>
                <input
                  className="w-full border border-slate-200 rounded-xl px-4 py-4 text-slate-800 text-3xl tracking-[0.6em] text-center font-bold focus:outline-none focus:ring-2"
                  placeholder="● ● ● ● ● ●"
                  value={pin}
                  onChange={e => { setPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                />
                {/* PIN strength indicator */}
                <div className="flex gap-1 mt-2">
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="flex-1 h-1 rounded-full transition-all"
                      style={{ background: pin.length >= i ? '#1B4F72' : '#e2e8f0' }} />
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Do not use simple sequences like 123456 or 111111
                </p>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                onClick={validatePin}
                disabled={pin.length !== 6}
                className="w-full text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition-all"
                style={{ background: '#1B4F72' }}
              >
                Continue →
              </button>
            </div>
          )}

          {/* Step 3: Confirm PIN */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl" style={{ background: '#eef3f8' }}>
                <p className="text-sm" style={{ color: '#1B4F72' }}>
                  Re-enter your PIN to confirm. Make sure to remember it!
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">Confirm Your PIN</label>
                <input
                  className="w-full border border-slate-200 rounded-xl px-4 py-4 text-slate-800 text-3xl tracking-[0.6em] text-center font-bold focus:outline-none focus:ring-2"
                  placeholder="● ● ● ● ● ●"
                  value={confirmPin}
                  onChange={e => { setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                onClick={confirmAndActivate}
                disabled={confirmPin.length !== 6}
                className="w-full text-white font-bold py-3 rounded-xl disabled:opacity-50 transition-all"
                style={{ background: '#1B4F72' }}
              >
                🔐 Activate Passport
              </button>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 4 && (
            <div className="text-center py-8 space-y-4">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto" style={{ background: '#d1fae5' }}>
                <CheckCircle size={48} style={{ color: '#16a34a' }} />
              </div>
              <h3 className="text-2xl font-bold text-slate-800">Passport Activated!</h3>
              <p className="text-slate-500 leading-relaxed">
                Your SevenStars SmartCare Passport is now active and secured with your PIN.
              </p>
              <div className="p-4 rounded-2xl text-left" style={{ background: '#eef3f8' }}>
                <p className="text-xs font-bold mb-2" style={{ color: '#1B4F72' }}>REMEMBER:</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Keep your <strong>6-digit PIN</strong> safe. You will need it to access your service history, book appointments, and use all passport features.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Transfer Ownership Flow ──────────────────────────────────
function TransferFlow({ passport, customer, onComplete, onCancel }) {
  const [step, setStep] = useState(1); // 1=pin, 2=code_shown, 3=new_owner, 4=done
  const [transferCode, setTransferCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [newPin, setNewPin] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('current'); // current | new

  const verifyPinForTransfer = async (pin) => {
    const ok = passportStore.checkPin(passport, pin);
    if (ok) {
      const code = await transferCodeStore.generate(passport.token, passport.customer_id);
      setTransferCode(code.code);
      setStep(2);
      return true;
    }
    return false;
  };

  const acceptTransfer = async () => {
    if (!enteredCode) { setError('Please enter the transfer code.'); return; }
    const codeObj = await transferCodeStore.verify(enteredCode);
    if (!codeObj || codeObj.token !== passport.token) { setError('Invalid or expired transfer code.'); return; }
    setError('');
    setStep(3);
  };

  const completeTransfer = async () => {
    if (!newName || !newMobile || newPin.length !== 6) { setError('Please fill in all fields.'); return; }
    const newCustomer = await customerStore.add({ name: newName, mobile: newMobile, municipality: '' });
    const pinHash = passportStore.hashPin(newPin);
    await passportStore.transferOwnership(passport.token, newCustomer.id, pinHash);
    await transferCodeStore.markUsed(enteredCode, newCustomer.id);
    setStep(4);
    setTimeout(onComplete, 2000);
  };

  if (step === 1 && mode === 'current') {
    return (
      <PinEntry
        title="Confirm Transfer"
        subtitle="Enter your PIN to generate a transfer code"
        onSuccess={verifyPinForTransfer}
        onCancel={onCancel}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.8)' }}>
      <div className="min-h-full flex items-end justify-center">
        <div className="w-full max-w-md bg-white rounded-t-3xl px-6 pt-6 pb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-bold text-slate-800 text-xl">Transfer Ownership</h2>
            <button onClick={onCancel}><X size={22} className="text-slate-400" /></button>
          </div>

          {/* Step 2: Show transfer code */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="p-5 rounded-2xl text-center" style={{ background: '#eef3f8', border: '2px solid #1B4F72' }}>
                <p className="text-sm text-slate-600 mb-2">Your Transfer Code (valid 24 hours):</p>
                <div className="text-4xl font-bold tracking-widest" style={{ color: '#1B4F72' }}>{transferCode}</div>
                <p className="text-xs text-slate-500 mt-2">Share this code with the new owner</p>
              </div>
              <div className="p-4 rounded-2xl" style={{ background: '#fff8f0', border: '1px solid #E87722' }}>
                <p className="text-sm" style={{ color: '#b45309' }}>
                  ⚠️ The new owner must scan this same QR code and enter this transfer code. Your access will end when transfer is complete.
                </p>
              </div>
              <div className="border-t pt-4">
                <p className="text-sm text-slate-600 mb-3">Are you the new owner entering the transfer code?</p>
                <button onClick={() => { setMode('new'); setStep(2.5); }} className="w-full font-semibold py-3 rounded-xl border-2 transition-all" style={{ borderColor: '#1B4F72', color: '#1B4F72' }}>
                  I am the New Owner — Enter Code
                </button>
              </div>
            </div>
          )}

          {/* Step 2.5: New owner enters code */}
          {step === 2.5 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Enter Transfer Code</label>
                <input
                  className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-xl tracking-widest text-center font-bold focus:outline-none uppercase"
                  placeholder="XXXXXXXX"
                  value={enteredCode}
                  onChange={e => { setEnteredCode(e.target.value.toUpperCase().replace(/\s/g,'')); setError(''); }}
                  maxLength={8}
                  autoFocus
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button onClick={acceptTransfer} className="w-full text-white font-semibold py-3 rounded-xl" style={{ background: '#1B4F72' }}>
                Verify Code →
              </button>
            </div>
          )}

          {/* Step 3: New owner details */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl" style={{ background: '#eef3f8' }}>
                <p className="text-sm" style={{ color: '#1B4F72' }}>Transfer code verified! Please enter your details to complete ownership transfer.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Full Name</label>
                <input className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" placeholder="Your full name" value={newName} onChange={e => setNewName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Mobile Number</label>
                <input className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" placeholder="09XX XXX XXXX" value={newMobile} onChange={e => setNewMobile(e.target.value)} type="tel" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Create Your PIN (6 digits)</label>
                <input className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-xl tracking-widest text-center font-bold focus:outline-none" placeholder="——————" value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g,'').slice(0,6))} type="password" maxLength={6} />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button onClick={completeTransfer} className="w-full text-white font-semibold py-3 rounded-xl" style={{ background: '#1B4F72' }}>
                Complete Transfer ✓
              </button>
            </div>
          )}

          {/* Done */}
          {step === 4 && (
            <div className="text-center py-8 space-y-4">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" style={{ background: '#d1fae5' }}>
                <CheckCircle size={40} style={{ color: '#16a34a' }} />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Transfer Complete!</h3>
              <p className="text-slate-500">Ownership has been successfully transferred.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN UNIT PASSPORT COMPONENT
// ============================================================
export default function UnitPassport({ token, onBack }) {
  const [passport, setPassport] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [customer, setCustomer] = useState(null); // eslint-disable-line
  const [warrantyInfo, setWarrantyInfo] = useState(null);
  const [pinVerified, setPinVerified] = useState(false);
  const [tab, setTab] = useState('home');
  const [showActivation, setShowActivation] = useState(false);
  const [showPinEntry, setShowPinEntry] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showDiagnose, setShowDiagnose] = useState(false);
  const [diagnoseSym, setDiagnoseSym] = useState(null);
  const [showReferral, setShowReferral] = useState(false);
  const [toast, setToast] = useState('');
  const [promotions, setPromotions] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [bookingForm, setBookingForm] = useState({ contact: '', issueCategory: 'Battery', description: '', visitDate: '', photos: [] });
  const [referralForm, setReferralForm] = useState({ buyerName: '', buyerContact: '' });

  const SHEET_URL = 'https://script.google.com/macros/s/AKfycbxhVgV_MZrnTVYrO8zeN_Ppl56aCTM0qU3T_FtJcuzQ85lUcsSJvGyIrsGuv8f8-zUM/exec';

  const loadData = useCallback(async () => {
    const p = await passportStore.getByToken(token);
    if (!p) return;
    setPassport(p);
    const v = await vehicleStore.getById(p.vehicle_id);
    setVehicle(v);
    if (v) setWarrantyInfo(vehicleStore.getWarrantyInfo(v));
    if (p.is_activated && p.customer_id) {
      const cust = await customerStore.getById(p.customer_id);
      setCustomer(cust);
    }
    setPromotions(await promotionStore.getActive());
    setServiceRecords(await serviceRecordStore.getByVehicle(p.vehicle_id));
    setServiceRequests(await serviceRequestStore.getByVehicle(p.vehicle_id));
    if (p.customer_id) setReferrals(await referralStore.getByReferrer(p.customer_id));
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  // Check if session already has PIN verified (30-min session)
  useEffect(() => {
    const sessionKey = `ss_pin_session_${token}`;
    const sessionData = sessionStorage.getItem(sessionKey);
    if (sessionData) {
      const { verifiedAt } = JSON.parse(sessionData);
      const thirtyMin = 30 * 60 * 1000;
      if (Date.now() - verifiedAt < thirtyMin) setPinVerified(true);
    }
  }, [token]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const requirePin = (action) => {
    if (pinVerified) { action(); return; }
    setPendingAction(() => action);
    setShowPinEntry(true);
  };

  const handlePinSuccess = (pin) => {
    const ok = passportStore.checkPin(passport, pin);
    if (ok) {
      setPinVerified(true);
      sessionStorage.setItem(`ss_pin_session_${token}`, JSON.stringify({ verifiedAt: Date.now() }));
      setShowPinEntry(false);
      if (pendingAction) { pendingAction(); setPendingAction(null); }
      return true;
    }
    return false;
  };

  const sendToSheet = async (formType, data) => {
    try {
      await fetch(SHEET_URL, {
        method: 'POST', mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formType, ...data, submittedAt: new Date().toLocaleString('en-PH', { timeZone: 'Asia/Manila' }) }),
      });
    } catch (e) { console.log('Sheet error:', e); }
  };

  const submitBooking = async () => {
    if (!bookingForm.contact || !bookingForm.description) { showToast('Please fill in contact number and description.'); return; }

    // Upload photos to Supabase Storage if any
    let photoUrls = [];
    if (bookingForm.photos && bookingForm.photos.length > 0) {
      showToast('Uploading photos...');
      for (const photo of bookingForm.photos) {
        try {
          const customerName = (customer?.name || 'unknown').replace(/\s+/g, '_');
          const fileName = `${passport.passport_id}/${customerName}_${Date.now()}_${photo.name.replace(/\s/g, '_')}`;
          const { data, error } = await supabase.storage
            .from('service-photos')
            .upload(fileName, photo, { upsert: true });
          if (!error && data) {
            const { data: urlData } = supabase.storage.from('service-photos').getPublicUrl(data.path);
            photoUrls.push(urlData.publicUrl);
          }
        } catch (e) { console.log('Photo upload error:', e); }
      }
    }

    await serviceRequestStore.add({
      vehicle_id: passport.vehicle_id,
      customer_id: passport.customer_id,
      passport_id: passport.passport_id,
      unit_model: vehicle?.model || '',
      warranty_status: warrantyInfo?.status || '',
      dealer_branch: 'SevenStars E-Bike Romblon',
      contact: bookingForm.contact,
      issue_category: bookingForm.issueCategory,
      description: bookingForm.description,
      visit_date: bookingForm.visitDate,
      photo_urls: photoUrls.join(','),
    });
    setServiceRequests(await serviceRequestStore.getByVehicle(passport.vehicle_id));
    await sendToSheet('SERVICE REQUEST', {
      passport_id: passport.passport_id,
      customer_name: customer?.name || '',
      unit_model: vehicle?.model,
      contact: bookingForm.contact,
      issue_category: bookingForm.issueCategory,
      description: bookingForm.description,
      visit_date: bookingForm.visitDate,
      photo_count: photoUrls.length,
    });
    setBookingForm({ contact: '', issueCategory: 'Battery', description: '', visitDate: '', photos: [] });
    showToast('✅ Service request submitted! We will contact you soon.');
    setTab('home');
  };

  const submitReferral = async () => {
    if (!referralForm.buyerName || !referralForm.buyerContact) { showToast('Please fill in all fields.'); return; }
    await referralStore.add({ referrer_id: passport.customer_id, referrer_name: customer?.name || '', buyer_name: referralForm.buyerName, buyer_contact: referralForm.buyerContact });
    setReferrals(await referralStore.getByReferrer(passport.customer_id));
    await sendToSheet('REFERRAL', {
      referrer_name: customer?.name || '',
      referrer_passport_id: passport.passport_id,
      buyer_name: referralForm.buyerName,
      buyer_contact: referralForm.buyerContact,
    });
    setShowReferral(false);
    setReferralForm({ buyerName: '', buyerContact: '' });
    showToast('Referral submitted! Thank you!');
  };

  if (!passport || !vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #081d31, #1B4F72)' }}>
        <div className="text-center text-white p-8">
          <AlertTriangle size={40} className="mx-auto mb-3 text-yellow-400" />
          <h2 className="font-bold text-xl mb-2">Passport Not Found</h2>
          <p className="text-white/70 text-sm mb-4">This QR code is invalid or has expired.</p>
          <button onClick={onBack} className="text-sm px-4 py-2 rounded-xl border border-white/30 text-white/80">← Go Back</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'home', label: 'Home', icon: '🏠' },
    { key: 'unit', label: 'Unit Info', icon: '🛵' },
    { key: 'book', label: 'Book', icon: '📅' },
    { key: 'promos', label: 'Promos', icon: '🏷️' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col max-w-md mx-auto">

      {/* ── HEADER ────────────────────────────────────────── */}
      <div className="px-5 pt-8 pb-5" style={{ background: 'linear-gradient(135deg, #081d31 0%, #1B4F72 100%)' }}>
        <div className="flex items-center justify-between mb-5">
          <button onClick={onBack} className="flex items-center gap-1 text-sm" style={{ color: '#E87722' }}>
            <ChevronLeft size={16} /> Exit
          </button>
          <div className="flex items-center gap-2">
            <img src={logo} alt="SevenStars" className="h-6 w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
            <span className="text-white text-sm font-semibold">SmartCare</span>
          </div>
        </div>

        {/* Activated: show personalized header */}
        {passport.is_activated && pinVerified && customer ? (
          <div>
            <p className="text-white/70 text-sm">Welcome back,</p>
            <h1 className="text-white text-2xl font-bold">{customer.name.split(' ')[0]}</h1>
            <div className="flex items-center gap-1 mt-1 text-white/60 text-xs">
              <MapPin size={11} /> {customer.municipality}
            </div>
          </div>
        ) : (
          <div>
            <p className="text-white/70 text-xs uppercase tracking-wide mb-1">SmartCare Passport</p>
            <h1 className="text-white text-xl font-bold">{passport.passport_id}</h1>
          </div>
        )}

        {/* Unit card */}
        <div className="mt-4 rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold" style={{ color: '#E87722' }}>YOUR E-BIKE</p>
              <p className="text-white font-bold text-base">{vehicle?.model}</p>
              <p className="text-white/50 text-xs mt-0.5">{vehicle?.color}</p>
            </div>
            <div className="text-right">
              {warrantyInfo && <WarrantyBadge status={warrantyInfo.status} />}
              {passport.is_activated && pinVerified ? (
                <p className="text-xs mt-1" style={{ color: '#4ade80' }}>🔓 Access Granted</p>
              ) : passport.is_activated ? (
                <button onClick={() => setShowPinEntry(true)} className="flex items-center gap-1 text-xs mt-1 text-white/60">
                  <Lock size={11} /> Enter PIN
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Activation CTA */}
        {!passport.is_activated && (
          <button
            onClick={() => setShowActivation(true)}
            className="mt-4 w-full text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95"
            style={{ background: '#E87722' }}
          >
            <Shield size={18} /> Activate My Passport
          </button>
        )}
      </div>

      {/* ── CONTENT ────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto pb-24">

        {/* HOME TAB */}
        {tab === 'home' && (
          <div className="p-4 space-y-4">

            {/* Not activated banner */}
            {!passport.is_activated && (
              <div className="rounded-2xl p-4" style={{ background: 'rgba(232,119,34,0.1)', border: '1.5px solid rgba(232,119,34,0.4)' }}>
                <div className="flex items-start gap-3">
                  <Lock size={18} style={{ color: '#E87722' }} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-white text-sm">Activate to unlock all features</p>
                    <p className="text-white/65 text-xs mt-0.5 leading-relaxed">Tap "Activate My Passport" above to set up your account and access Book Service, Diagnose, Service Log, and Refer & Earn.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Quick actions — ALL require activation */}
            <div className="grid grid-cols-2 gap-3">

              {/* Book Service — requires activation + PIN */}
              <button
                onClick={() => {
                  if (!passport.is_activated) { showToast('Please activate your passport first.'); return; }
                  requirePin(() => setTab('book'));
                }}
                className="bg-white rounded-2xl p-5 flex flex-col items-center gap-2 shadow-sm border border-slate-100 transition-all active:scale-95 relative"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#eef3f8' }}>
                  <Calendar size={24} style={{ color: '#1B4F72' }} />
                </div>
                <span className="font-semibold text-sm text-slate-700">Book Service</span>
                {(!passport.is_activated || !pinVerified) && <Lock size={11} className="absolute top-3 right-3 text-slate-400" />}
              </button>

              {/* Diagnose Issue — requires activation + PIN */}
              <button
                onClick={() => {
                  if (!passport.is_activated) { showToast('Please activate your passport first.'); return; }
                  requirePin(() => setShowDiagnose(true));
                }}
                className="bg-white rounded-2xl p-5 flex flex-col items-center gap-2 shadow-sm border border-slate-100 transition-all active:scale-95 relative"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-orange-50">
                  <AlertTriangle size={24} style={{ color: '#E87722' }} />
                </div>
                <span className="font-semibold text-sm text-slate-700">Diagnose Issue</span>
                {(!passport.is_activated || !pinVerified) && <Lock size={11} className="absolute top-3 right-3 text-slate-400" />}
              </button>

              {/* Service Log — requires activation + PIN */}
              <button
                onClick={() => {
                  if (!passport.is_activated) { showToast('Please activate your passport first.'); return; }
                  requirePin(() => setTab('history'));
                }}
                className="bg-white rounded-2xl p-5 flex flex-col items-center gap-2 shadow-sm border border-slate-100 transition-all active:scale-95 relative"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-50">
                  <Wrench size={24} className="text-slate-500" />
                </div>
                <span className="font-semibold text-sm text-slate-700">Service Log</span>
                {(!passport.is_activated || !pinVerified) && <Lock size={11} className="absolute top-3 right-3 text-slate-400" />}
              </button>

              {/* Refer & Earn — requires activation + PIN */}
              <button
                onClick={() => {
                  if (!passport.is_activated) { showToast('Please activate your passport first.'); return; }
                  requirePin(() => setShowReferral(true));
                }}
                className="bg-white rounded-2xl p-5 flex flex-col items-center gap-2 shadow-sm border border-slate-100 transition-all active:scale-95 relative"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-50">
                  <Gift size={24} className="text-green-600" />
                </div>
                <span className="font-semibold text-sm text-slate-700">Refer & Earn</span>
                {(!passport.is_activated || !pinVerified) && <Lock size={11} className="absolute top-3 right-3 text-slate-400" />}
              </button>
            </div>

            {/* Maintenance status */}
            {serviceRecords.length > 0 && (() => {
              const last = serviceRecords.sort((a, b) => b.date.localeCompare(a.date))[0];
              const days = last.next_service_date ? Math.ceil((new Date(last.next_service_date) - new Date()) / (1000*60*60*24)) : null;
              const badge = days === null ? null : days < 0 ? { label: 'Overdue', bg: '#fee2e2', color: '#dc2626' } : days < 14 ? { label: 'Due Soon', bg: '#fef3c7', color: '#d97706' } : { label: 'Good', bg: '#d1fae5', color: '#16a34a' };
              return (
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-slate-700">Maintenance Status</h3>
                    {badge && <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>}
                  </div>
                  <p className="text-sm text-slate-500">Last service: <span className="font-medium text-slate-700">{last.date}</span> · {last.type}</p>
                  {last.next_service_date && <p className="text-sm text-slate-500 mt-0.5">Next recommended: <span className="font-medium text-slate-700">{last.next_service_date}</span></p>}
                </div>
              );
            })()}

            {/* Active requests */}
            {serviceRequests.filter(r => r.status !== 'completed' && r.status !== 'cancelled').length > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border-l-4" style={{ borderLeftColor: '#E87722' }}>
                <h3 className="font-bold text-slate-700 mb-2">Active Service Request</h3>
                {serviceRequests.filter(r => r.status !== 'completed' && r.status !== 'cancelled').map(r => (
                  <div key={r.id} className="text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">{r.issue_category}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 capitalize">{r.status}</span>
                    </div>
                    {r.visit_date && <p className="text-slate-500 mt-0.5">Visit Date: {r.visit_date}</p>}
                  </div>
                ))}
              </div>
            )}

            {/* Referrals (if activated + pin verified) */}
            {pinVerified && referrals.length > 0 && (
              <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                <h3 className="font-bold text-slate-700 mb-2">My Referrals</h3>
                {referrals.map(r => (
                  <div key={r.id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-slate-700">{r.buyer_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{r.status}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Transfer ownership — PIN required */}
            {passport.is_activated && (
              <button
                onClick={() => requirePin(() => setShowTransfer(true))}
                className="w-full bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center justify-between transition-all active:scale-95"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-red-50">
                    <ArrowRight size={20} className="text-red-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-slate-700 text-sm">Transfer Ownership</p>
                    <p className="text-slate-400 text-xs">Sell or give your e-bike</p>
                  </div>
                </div>
                {!pinVerified && <Lock size={14} className="text-slate-400" />}
              </button>
            )}
          </div>
        )}

        {/* UNIT INFO TAB */}
        {tab === 'unit' && (
          <div className="p-4 space-y-4">
            {/* Public unit info */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-800 mb-4 text-lg">Unit Information</h3>
              {[
                ['Model', vehicle?.model],
                ['Color', vehicle?.color || '—'],
                ['Battery Type', vehicle?.battery_type || 'Lead Acid'],
                ['Warranty Status', warrantyInfo?.status === 'active' ? 'Active' : warrantyInfo?.status === 'expiring' ? 'Expiring Soon' : 'For Renewal'],
                ['Dealer', 'SevenStars E-Bike Romblon'],
                ['Contact', '0999 341 4528'],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between py-2.5 border-b border-slate-50 last:border-0 text-sm">
                  <span className="text-slate-500">{label}</span>
                  <span className="font-medium text-slate-800">{val}</span>
                </div>
              ))}
            </div>

            {/* Warranty card — PIN required for full details */}
            <div className="rounded-2xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #1B4F72, #081d31)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Shield size={16} className="text-white/60" />
                <span className="text-xs font-bold uppercase tracking-wide text-white/60">Digital Warranty Card</span>
              </div>
              <div className="text-2xl font-bold mb-1">{vehicle?.model}</div>
              <div className="text-white/60 text-sm mb-4">{vehicle?.color}</div>
              {pinVerified ? (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.1)' }}>
                      <p className="text-white/50 text-xs">Start Date</p>
                      <p className="text-white font-bold">{vehicle?.purchase_date}</p>
                    </div>
                    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.1)' }}>
                      <p className="text-white/50 text-xs">End Date</p>
                      <p className="text-white font-bold">{warrantyInfo?.endDate}</p>
                    </div>
                  </div>
                  <WarrantyBadge status={warrantyInfo?.status} />
                </>
              ) : (
                <button
                  onClick={() => requirePin(() => {})}
                  className="flex items-center gap-2 text-white/70 text-sm border border-white/20 px-4 py-2 rounded-xl"
                >
                  <Lock size={14} /> Enter PIN to view warranty dates
                </button>
              )}
            </div>

            {/* Coverage */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
              <h3 className="font-bold text-slate-700 mb-3">Warranty Coverage</h3>
              {['Motor (manufacturing defects)', 'Battery (factory defects)', 'Controller and electrical system (factory defects)', 'Free lifetime check-up'].map(i => (
                <div key={i} className="flex items-start gap-2 py-1.5 text-sm">
                  <CheckCircle size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
                  <span className="text-slate-600">{i}</span>
                </div>
              ))}
            </div>

            <div className="bg-red-50 rounded-2xl p-4 border border-red-100">
              <h3 className="font-bold text-red-700 mb-2">Not Covered</h3>
              {['Damage from accidents, misuse, or human error', 'Normal wear (tires, brake pads)', 'Water damage', 'Unauthorized repairs or modifications', 'Lost or stolen unit'].map(i => (
                <div key={i} className="flex items-start gap-2 py-1 text-sm">
                  <X size={14} className="flex-shrink-0 mt-0.5 text-red-500" />
                  <span className="text-red-600">{i}</span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-center">
              <p className="text-slate-500 text-sm mb-3">Need warranty support?</p>
              <a href="tel:+639993414528" className="inline-flex items-center gap-2 text-white font-semibold px-5 py-2.5 rounded-xl" style={{ background: '#1B4F72' }}>
                <Phone size={16} /> Call 0999 341 4528
              </a>
            </div>
          </div>
        )}

        {/* SERVICE HISTORY TAB — PIN required */}
        {tab === 'history' && (
          <div className="p-4 space-y-4">
            <h2 className="text-lg font-bold text-slate-800">Service History</h2>
            {serviceRecords.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-slate-100 shadow-sm">
                <Wrench size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="font-semibold text-slate-700">No service records yet.</p>
                <p className="text-slate-500 text-sm mt-1">Your e-bike may be due for its first maintenance. Contact SevenStars to schedule a visit!</p>
                <a href="tel:+639993414528" className="inline-flex items-center gap-2 mt-4 text-white text-sm font-semibold px-4 py-2.5 rounded-xl" style={{ background: '#1B4F72' }}>
                  <Phone size={14} /> Contact Us
                </a>
              </div>
            ) : (
              serviceRecords.sort((a, b) => b.date.localeCompare(a.date)).map(r => (
                <div key={r.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-bold text-slate-800">{r.type}</p>
                      <p className="text-xs text-slate-500">{r.date} · {r.technician_name}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Service</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{r.technician_notes}</p>
                  {r.parts_replaced && r.parts_replaced !== 'None' && (
                    <p className="text-xs bg-slate-50 rounded-lg p-2 text-slate-500">Parts: {r.parts_replaced}</p>
                  )}
                  {r.next_service_date && (
                    <div className="flex items-center gap-1.5 mt-2 text-xs" style={{ color: '#1B4F72' }}>
                      <Clock size={11} /> Next service: {r.next_service_date}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* BOOK SERVICE TAB */}
        {tab === 'book' && (
          <div className="p-4 space-y-4">
            <h2 className="text-lg font-bold text-slate-800">Book a Service</h2>

            {/* Auto-filled unit context */}
            <div className="rounded-2xl p-3" style={{ background: '#eef3f8', border: '1px solid #9bb5d5' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#1B4F72' }}>Unit Context (Auto-filled from QR)</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                <div>Model: <span className="font-semibold text-slate-800">{vehicle?.model}</span></div>
                <div>Warranty: <span className="font-semibold text-slate-800">{warrantyInfo?.status === 'active' ? 'Active' : 'For Renewal'}</span></div>
                <div>Passport: <span className="font-semibold text-slate-800">{passport.passport_id}</span></div>
                <div>Dealer: <span className="font-semibold text-slate-800">Romblon</span></div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Contact Number *</label>
                <input className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none focus:ring-2" placeholder="09XX XXX XXXX" value={bookingForm.contact} onChange={e => setBookingForm(f => ({ ...f, contact: e.target.value }))} type="tel" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Issue Category *</label>
                <select className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" value={bookingForm.issueCategory} onChange={e => setBookingForm(f => ({ ...f, issueCategory: e.target.value }))}>
                  {ISSUE_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Describe your concern *</label>
                <textarea className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" rows={3} placeholder="What is happening with your e-bike?" value={bookingForm.description} onChange={e => setBookingForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Visit Date</label>
                <input type="date" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" value={bookingForm.visitDate} onChange={e => setBookingForm(f => ({ ...f, visitDate: e.target.value }))} />
              </div>
              {/* Photo upload — Supabase Storage */}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  📷 Add Photos (optional)
                </label>
                <div
                  className="rounded-xl border-2 border-dashed border-slate-200 p-4 text-center cursor-pointer hover:border-blue-300 transition-all"
                  onClick={() => document.getElementById('photo-upload').click()}
                >
                  {bookingForm.photos && bookingForm.photos.length > 0 ? (
                    <div>
                      <div className="flex gap-2 flex-wrap justify-center mb-2">
                        {bookingForm.photos.map((photo, i) => (
                          <div key={i} className="relative">
                            <img src={URL.createObjectURL(photo)} alt="" className="w-16 h-16 object-cover rounded-lg" />
                            <button
                              onClick={e => { e.stopPropagation(); setBookingForm(f => ({ ...f, photos: f.photos.filter((_, j) => j !== i) })); }}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                            >×</button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400">Tap to add more ({bookingForm.photos.length}/3)</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={20} className="text-slate-300 mx-auto mb-1" />
                      <p className="text-sm text-slate-500 font-medium">Tap to add photos</p>
                      <p className="text-xs text-slate-400 mt-0.5">Show us the problem — up to 3 photos</p>
                    </div>
                  )}
                </div>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={e => {
                    const files = Array.from(e.target.files).slice(0, 3);
                    setBookingForm(f => ({ ...f, photos: [...(f.photos || []), ...files].slice(0, 3) }));
                  }}
                />
              </div>
              <button onClick={submitBooking} className="w-full text-white font-bold py-3 rounded-xl transition-all active:scale-95" style={{ background: '#1B4F72' }}>
                Submit Service Request
              </button>
            </div>

            {/* Past requests */}
            {serviceRequests.length > 0 && (
              <div>
                <h3 className="font-bold text-slate-700 mb-3">My Requests</h3>
                {serviceRequests.map(r => (
                  <div key={r.id} className="bg-white rounded-2xl p-4 mb-3 shadow-sm border border-slate-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-700 text-sm">{r.issue_category}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>
                        {r.visit_date && <p className="text-xs text-slate-400 mt-1">Visit: {r.visit_date}</p>}
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 capitalize">{r.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PROMOS TAB */}
        {tab === 'promos' && (
          <div className="p-4 space-y-4">
            <h2 className="text-lg font-bold text-slate-800">Current Promotions</h2>
            {promotions.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
                <Tag size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No active promotions right now.</p>
              </div>
            ) : (
              promotions.map(p => (
                <div key={p.id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#fff8f0' }}>
                      <Tag size={18} style={{ color: '#E87722' }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-slate-800">{p.title}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 mt-1 inline-block">{p.category}</span>
                      <p className="text-sm text-slate-600 mt-2">{p.description}</p>
                      {p.valid_until && <p className="text-xs text-slate-400 mt-1">Valid until: {p.valid_until}</p>}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-center">
              <p className="font-semibold text-slate-700 mb-2">Questions about a promo?</p>
              <a href="tel:+639993414528" className="inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-2.5 rounded-xl" style={{ background: '#1B4F72' }}>
                <Phone size={14} /> Call Us
              </a>
            </div>
          </div>
        )}
      </div>

      {/* ── MODALS ─────────────────────────────────────────── */}

      {/* Activation */}
      {showActivation && (
        <ActivationFlow
          passport={passport}
          vehicle={vehicle}
          onComplete={() => { setShowActivation(false); loadData(); showToast('Passport activated! 🎉'); }}
          onCancel={() => setShowActivation(false)}
        />
      )}

      {/* PIN Entry */}
      {showPinEntry && passport.is_activated && (
        <PinEntry
          onSuccess={handlePinSuccess}
          onCancel={() => { setShowPinEntry(false); setPendingAction(null); }}
        />
      )}

      {/* Transfer Ownership */}
      {showTransfer && (
        <TransferFlow
          passport={passport}
          customer={customer}
          onComplete={() => { setShowTransfer(false); setPinVerified(false); loadData(); showToast('Ownership transferred.'); setTab('home'); }}
          onCancel={() => setShowTransfer(false)}
        />
      )}

      {/* Diagnose */}
      {showDiagnose && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end overflow-y-auto">
          <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 mt-20 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800 text-lg">Help Me Diagnose</h3>
              <button onClick={() => { setShowDiagnose(false); setDiagnoseSym(null); }}><X size={22} className="text-slate-400" /></button>
            </div>
            {!diagnoseSym ? (
              <>
                <p className="text-slate-500 text-sm mb-4">Select the symptom you're experiencing:</p>
                {Object.keys(DIAGNOSE).map(sym => (
                  <button key={sym} onClick={() => setDiagnoseSym(sym)}
                    className="w-full text-left p-3.5 rounded-xl border border-slate-100 hover:border-orange-300 hover:bg-orange-50 transition-all text-sm font-medium text-slate-700 flex justify-between items-center mb-2">
                    {sym} <ArrowRight size={16} className="text-slate-400" />
                  </button>
                ))}
              </>
            ) : (
              <>
                <button onClick={() => setDiagnoseSym(null)} className="text-sm mb-4 flex items-center gap-1" style={{ color: '#1B4F72' }}>
                  ← Back
                </button>
                <h4 className="font-bold text-slate-800 mb-4">{diagnoseSym}</h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Possible Causes</p>
                    {DIAGNOSE[diagnoseSym].causes.map((c, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-slate-600 mb-1.5">
                        <Info size={14} style={{ color: '#E87722' }} className="mt-0.5 flex-shrink-0" />{c}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">What To Do</p>
                    {DIAGNOSE[diagnoseSym].advice.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-slate-600 mb-1.5">
                        <CheckCircle size={14} style={{ color: '#16a34a' }} className="mt-0.5 flex-shrink-0" />{a}
                      </div>
                    ))}
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-amber-700 text-xs">This is basic guidance only. For safety, please contact a SevenStars technician for proper inspection.</p>
                    </div>
                  </div>
                  <button onClick={() => { setShowDiagnose(false); setDiagnoseSym(null); setTab('book'); }}
                    className="w-full text-white font-bold py-3 rounded-xl" style={{ background: '#1B4F72' }}>
                    Book a Technician
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Referral */}
      {showReferral && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800 text-lg">Refer a Friend</h3>
              <button onClick={() => setShowReferral(false)}><X size={22} className="text-slate-400" /></button>
            </div>
            <p className="text-slate-500 text-sm mb-4">Refer someone who buys an e-bike and earn ₱500 store credit!</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Friend's Name *</label>
                <input className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" placeholder="Full name" value={referralForm.buyerName} onChange={e => setReferralForm(f => ({ ...f, buyerName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">Friend's Contact Number *</label>
                <input className="w-full border border-slate-200 rounded-xl px-4 py-3 text-slate-800 focus:outline-none" placeholder="09XX XXX XXXX" value={referralForm.buyerContact} onChange={e => setReferralForm(f => ({ ...f, buyerContact: e.target.value }))} type="tel" />
              </div>
              <button onClick={submitReferral} className="w-full text-white font-bold py-3 rounded-xl" style={{ background: '#1B4F72' }}>Submit Referral</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-5 py-3 rounded-2xl text-sm shadow-lg z-50 max-w-xs text-center">
          {toast}
        </div>
      )}

      {/* ── BOTTOM NAV ─────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white border-t border-slate-100 px-2 py-2 z-40 flex justify-around">
        {tabs.map(t => (
          <button key={t.key}
            onClick={() => {
              if (t.key === 'history') { requirePin(() => setTab('history')); }
              else setTab(t.key);
            }}
            className="flex flex-col items-center gap-0.5 py-2 px-3 rounded-xl transition-all cursor-pointer"
            style={{ color: tab === t.key ? '#1B4F72' : '#94a3b8', background: tab === t.key ? '#eef3f8' : 'transparent' }}
          >
            <span className="text-lg">{t.icon}</span>
            <span className="text-xs font-medium">{t.label}</span>
            {t.key === 'history' && !pinVerified && <Lock size={8} style={{ color: '#E87722' }} />}
          </button>
        ))}
      </div>
    </div>
  );
}
